import type { PaddingMarginValues } from '@/types/layout';
import {
  BUTTON_NODE_NAME,
  CONTENT_BLOCK_NAME,
  ICON_NODE_NAME,
  IMAGE_NODE_NAME,
  VIDEO_NODE_NAME,
  EMBED_NODE_NAME,
  TEXT_NODE_NAME,
  FORM_NODE_NAME,
  FORM_SUBMIT_BUTTON_NODE_NAME,
  FORM_EMAIL_FIELD_NODE_NAME,
  COUNTDOWN_NODE_NAME,
  POPUP_NODE_NAME,
} from './constants';
import { type Descendant, Element as SlateElement } from 'slate';
import { ROOT_NODE, type Node } from '@craftjs/core';
import type { PositionFlags } from '@/components/userComponents/Resizer';
import { useContainerHelper } from '@/hooks/useContainerHelper';
import { getElementDimensionsWithPadding } from './numToMeasurement';
import EditorText from '@/components/userComponents/Text';
import EditorButton from '@/components/userComponents/Button';
import EditorPage from '@/components/userComponents/Page';
import EditorContainer from '@/components/userComponents/Container';
import EditorImage from '@/components/userComponents/Image';
import EditorVideo from '@/components/userComponents/Video';
import EditorIcon from '@/components/userComponents/Icon';
import EditorEmbed from '@/components/userComponents/Embed';
import EditorForm from '@/components/userComponents/Form';
import EditorFormTextField from '@/components/userComponents/Form/fields/FormTextField';
import EditorFormEmailField from '@/components/userComponents/Form/fields/FormEmailField';
import EditorFormConsentField from '@/components/userComponents/Form/fields/FormConsentField';
import EditorFormDropdownField from '@/components/userComponents/Form/fields/FormDropdownField';
import EditorFormPhoneField from '@/components/userComponents/Form/fields/FormPhoneField';
import EditorFormSelectionField from '@/components/userComponents/Form/fields/FormSelectionField';
import EditorFormSubmitButton from '@/components/userComponents/Form/buttons/FormSubmitButton';
import type { ButtonType } from '@builder4/shared/styles/constants';
import type { Breakpoint } from '@builder4/shared/types/layout';
import EditorCountdown from '@/components/userComponents/Countdown';
import EditorPopup from '@/components/userComponents/Popup';
import AiGenerationLoader from '@/components/userComponents/AiGenerationLoader';
import { getBreakpointProp } from '@builder4/shared/utils/breakpointUtils';
import type { ContainerProps } from '@builder4/shared/components/Container';

export const mapMarginPaddingArrayToObject = (input: number[]): PaddingMarginValues => {
  const [top = 0, right = 0, bottom = 0, left = 0] = input || [];
  const all = top === right && top === bottom && top === left ? top : 0;

  return { top, right, bottom, left, all };
};

/**
 * Determines if a node is an element whose height can be adjusted.
 * Currently, only container , form ,countdown and image nodes are considered height adjustable.
 *
 * @param node - The Craft.js node to check.
 * @returns True if the node is a container or image node, false otherwise.
 */
export const isHeightAdjustableElement = (node: Node) => {
  return (
    isContainerNode(node) ||
    isImageNode(node) ||
    isVideoNode(node) ||
    isEmbedNode(node) ||
    isFormNode(node) ||
    isCountdownNode(node) ||
    isIconNode(node)
  );
};
/**
 * Determines if a node is an element that has width set as a percentage.
 * Checks if the width property is a string containing a '%' character.
 *
 * @param node - The Craft.js node to check.
 * @returns True if the node has width set as a percentage, false otherwise.
 */
export const hasPercentageWidth = (width: string) => {
  return typeof width === 'string' && width.includes('%');
};

export const isRelativelyFillableElementV2 = (node: Node, breakpoint: Breakpoint) => {
  const position = getBreakpointProp(
    node.data.props as Partial<ContainerProps>,
    'position',
    breakpoint,
  );

  if (isButtonNode(node)) {
    const fillWidth = getBreakpointProp(
      node.data.props as Partial<{ fillWidth?: boolean }>,
      'fillWidth',
      breakpoint,
    );
    return fillWidth;
  }

  return (
    isContainerNode(node) ||
    isImageNode(node) ||
    isTextBlockNode(node) ||
    isFormNode(node) ||
    isVideoNode(node) ||
    isCountdownNode(node) ||
    isIconNode(node) ||
    (isEmbedNode(node) && (position === 'relative' || position === 'sticky'))
  );
};

export const isPotentiallyFillableElement = (node: Node) => {
  return (
    isContainerNode(node) ||
    isImageNode(node) ||
    isVideoNode(node) ||
    isEmbedNode(node) ||
    isTextBlockNode(node) ||
    isFormNode(node) ||
    isButtonNode(node) ||
    isCountdownNode(node) ||
    isIconNode(node)
  );
};

export const isContainerNode = (node: Node) => {
  return node.data.displayName === CONTENT_BLOCK_NAME;
};

export const isImageNode = (node: Node) => {
  return node.data.displayName === IMAGE_NODE_NAME;
};
export const isVideoNode = (node: Node) => {
  return node.data.displayName === VIDEO_NODE_NAME;
};
export const isIconNode = (node: Node) => {
  return node.data.displayName === ICON_NODE_NAME;
};
export const isEmbedNode = (node: Node) => {
  return node.data.displayName === EMBED_NODE_NAME;
};
export const isFormNode = (node: Node) => {
  return node.data.displayName === FORM_NODE_NAME;
};

export const isFormSubmitButtonNode = (node: Node) => {
  return node.data.displayName === FORM_SUBMIT_BUTTON_NODE_NAME;
};

export const isButtonNode = (node: Node) => {
  return node.data.displayName === BUTTON_NODE_NAME;
};

export const isCountdownNode = (node: Node) => {
  return node.data.displayName === COUNTDOWN_NODE_NAME;
};

export const isPopupNode = (node: Node) => {
  return node.data.displayName === POPUP_NODE_NAME;
};

/**
 * Extracts the numeric suffix from a popup name.
 * Returns the number if the name matches "Popup N" pattern, otherwise null.
 */
const extractPopupNumber = (name: string): number | null => {
  const match = name.match(/^Popup\s+(\d+)$/);
  return match ? parseInt(match[1], 10) : null;
};

/**
 * Finds the next available popup number by examining all existing popups.
 * Returns the highest existing number + 1, or 1 if no numbered popups exist.
 */
export const getNextPopupNumber = (
  query: NodeQuery & { getNodes: () => Record<string, Node> },
): number => {
  const allNodes = query.getNodes();
  let maxNumber = 0;

  for (const node of Object.values(allNodes)) {
    if (!isPopupNode(node)) continue;

    const customDisplayName = node.data.custom?.displayName as string | undefined;
    const nameToCheck = customDisplayName || node.data.displayName;
    const popupNumber = extractPopupNumber(nameToCheck);

    if (popupNumber !== null && popupNumber > maxNumber) {
      maxNumber = popupNumber;
    }
  }

  return maxNumber + 1;
};

export const isTextBlockNode = (node: Node) => {
  return node.data.displayName === TEXT_NODE_NAME;
};

/**
 * Checks if the given node is a container and has no child nodes.
 *
 * @param node - The Craft.js node to check.
 * @returns True if the node has no children, false otherwise.
 */
export const emptyContainer = (node: Node): boolean => {
  const childs = node.data?.nodes || [];
  return childs.length === 0;
};

/**
 * Get the adjacent fillable sibling node in a row (for resize/dimension constraints).
 * Same rule as Resizer setupSiblingTracking: preferredDirection 'right' -> next sibling,
 * 'left' -> previous, else default next then prev.
 */
export const getAdjacentFillableSibling = (
  currentNodeId: string,
  fillableSiblings: Node[],
  preferredDirection?: 'left' | 'right',
): Node | null => {
  if (fillableSiblings.length < 2) return null;
  const currentIndex = fillableSiblings.findIndex((s) => s.id === currentNodeId);
  if (currentIndex === -1) return null;
  let siblingIndex: number;
  if (preferredDirection === 'right') {
    siblingIndex = currentIndex + 1;
  } else if (preferredDirection === 'left') {
    siblingIndex = currentIndex - 1;
  } else {
    siblingIndex = currentIndex + 1;
    if (siblingIndex >= fillableSiblings.length) siblingIndex = currentIndex - 1;
  }
  if (siblingIndex < 0 || siblingIndex >= fillableSiblings.length) return null;
  return fillableSiblings[siblingIndex];
};

// Helper function to check if a node is a page
export const isPageNode = (node: Node) => {
  return (
    node && (node.data.type === 'Page' || node.data.displayName === 'Page' || node.id === ROOT_NODE)
  );
};

/**
 * Determines if a node can be duplicated.
 * @param node - Node to check.
 * @returns True if the node can be duplicated.
 */
export const canDuplicateNode = (node: Node): boolean => {
  if (node.data.displayName === FORM_EMAIL_FIELD_NODE_NAME) {
    return !node.data.props?.primaryEmailField;
  } else if (node.data.displayName === FORM_SUBMIT_BUTTON_NODE_NAME) {
    return false;
  }
  return true;
};

/**
 * Determines whether a placeholder is currently being shown for a given node.
 * - For container nodes, returns true if the container is empty (has no child nodes).
 * - For image nodes, returns true if the image source is empty.
 * - For other node types, returns undefined (no placeholder applicable).
 *
 * @param node - The Craft.js node to check.
 * @returns True if a placeholder is currently shown, false if not, undefined for unsupported node types.
 */

export const showingPlaceholder = (node: Node) => {
  if (isContainerNode(node)) {
    return emptyContainer(node);
  } else if (isImageNode(node)) {
    return node.data.props.src === '';
  } else if (isVideoNode(node)) {
    return !node.data.props.url && !node.data.props.embedCode;
  } else if (isEmbedNode(node)) {
    return !node.data.props.url && !node.data.props.htmlCode;
  }
};

export const hasFixedHeightV2 = (height: string) => {
  return height !== 'auto' && height !== 'min-content';
};

/**
 * Converts a width string (e.g., "50%", "320px") to a number, rounded to the nearest integer.
 *
 * @param width - The width string to parse (may include "%" or "px" units).
 * @returns The width as a number.
 */
export const parseWidthToNumber = (width: string): number => {
  return Math.round(parseFloat(String(width).replace('%', '').replace('px', '')));
};
/**
 * Get the width of the content box of a node.
 * @param node - The Craft.js node to get the content box width from.
 * This width excludes root div which has margin styles. It gives thw width of div which has actuall content
 * @returns The width of the content box as a number including padding and excluding border, rounded to the nearest integer.
 */
export const getNodeContentBoxWidthInPx = (node: Node): number => {
  if (!node.dom) return 0;
  const contentNode = getBoundaryDomElement(node.dom);
  return getElementDimensionsWithPadding(contentNode).width;
};

export function getCotentHeight(node: Node) {
  const nodeHeight = node.data.props.height;
  const nodePadding = node.data.props.padding;
  const borderWidth = node.data.props.borderWidth;
  return (
    parseInt(String(nodeHeight).replace('px', '')) -
    (nodePadding[0] + nodePadding[2] + borderWidth * 2)
  );
}
export const slateValueToText = (value: Descendant[]): string => {
  return value
    .map((node) => {
      if (SlateElement.isElement(node)) {
        return node.children.map((child) => child.text).join('');
      }
      return '';
    })
    .join('\n');
};
// Define types for Craft.js query interface
interface NodeQuery {
  node: (nodeId: string) => {
    get: () => Node | null;
  };
}

// Helper function to get sibling count
export const getSiblingsCount = (node: Node, query: NodeQuery): number => {
  const parentId = node.data.parent;

  if (!parentId) {
    return 0;
  }

  try {
    const parentNode = query.node(parentId);
    if (parentNode && parentNode.get()) {
      return parentNode.get()?.data.nodes?.length || 0;
    }
  } catch (error) {
    console.warn('Error getting siblings count:', error);
  }

  return 0;
};

export function getAllowedResizeControls({
  node,
  isActive,
  parentDirection,
  parentNode,
  id,
}: {
  node: Node;
  isActive: boolean;
  parentDirection?: string;
  parentNode?: Node;
  id: string;
}): Partial<PositionFlags> {
  const { getIndexInParent } = useContainerHelper();
  const allowedControls: Partial<PositionFlags> = {};

  if (!isActive) return allowedControls;
  if (!parentNode) return allowedControls;

  const index = getIndexInParent(id) ?? -1;
  const lastIndex = parentNode?.data.nodes.length ? parentNode.data.nodes.length - 1 : 0;

  const enableHorizontal = parentDirection === 'row';

  if (enableHorizontal) {
    allowedControls.left = index > 0;
    allowedControls.right = index < lastIndex;
  }

  if (
    isImageNode(node as Node) ||
    isContainerNode(node as Node) ||
    isVideoNode(node as Node) ||
    isFormNode(node as Node) ||
    isIconNode(node as Node) ||
    isEmbedNode(node as Node) ||
    isFormNode(node as Node) ||
    isPopupNode(node as Node)
  ) {
    allowedControls.top = true;
    allowedControls.bottom = true;
  }

  if (isPopupNode(node as Node)) {
    allowedControls.left = true;
    allowedControls.right = true;
  }

  return allowedControls;
}
export const getBoundaryDomElement = (dom: HTMLElement): HTMLElement => {
  return dom.classList.contains('boundary-indicator')
    ? dom
    : findChildByClass(dom, 'boundary-indicator') || dom;
};
/**
 * Finds the first descendant of the given element with the specified class name.
 * @param parent The parent HTMLElement to search within.
 * @param className The class name to search for.
 * @returns The first matching HTMLElement, or null if not found.
 */
function findChildByClass(parent: HTMLElement | null, className: string): HTMLElement | null {
  if (!parent) return null;
  return parent.querySelector(`.${className}`);
}
export const getNodeTopLeftInParentElement = (
  parentNode: Node,
  currentNode: Node,
  scale: number,
): { top: number; left: number; right: number; bottom: number } => {
  if (!parentNode?.dom || !currentNode.dom)
    return {
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
    };
  scale = scale || 1;

  const elementBoundaryDom = getBoundaryDomElement(currentNode.dom);
  const parentBoundaryDom = getBoundaryDomElement(parentNode.dom);

  const elementRect = elementBoundaryDom.getBoundingClientRect();
  const parentRect = parentBoundaryDom.getBoundingClientRect();

  // Calculate the offset of the element relative to its parent
  const top = Math.round((elementRect.top - parentRect.top) / scale);
  const left = Math.round((elementRect.left - parentRect.left) / scale);
  const right = Math.round((parentRect.right - elementRect.right) / scale);
  const bottom = Math.round((parentRect.bottom - elementRect.bottom) / scale);

  return {
    top,
    left,
    right,
    bottom,
  };
};
export type ControlsFixedPosition = {
  top: string;
  left: string;
  right: string;
  bottom: string;
  width: string;
  height: string;
  centerX: string;
  centerY: string;
};
export const getDomFixedPosition = (dom: HTMLElement | null): ControlsFixedPosition => {
  if (!dom)
    return {
      top: '0px',
      left: '0px',
      right: '0px',
      bottom: '0px',
      centerX: '0px',
      centerY: '0px',
      width: '0px',
      height: '0px',
    };

  const el = getBoundaryDomElement(dom);

  if (!el) {
    return {
      top: '0px',
      left: '0px',
      right: '0px',
      bottom: '0px',
      centerX: '0px',
      centerY: '0px',
      width: '0px',
      height: '0px',
    };
  }

  const { top, left, width, height } = el.getBoundingClientRect();
  return {
    top: `${top}px`,
    left: `${left}px`,
    right: `${left + width}px`,
    bottom: `${top + height}px`,
    width: `${width}px`,
    height: `${height}px`,
    centerX: `${left + width / 2}px`,
    centerY: `${top + height / 2}px`,
  };
};
// ✅ Helper function to determine type
export const detectInputType = (value: number | string | readonly string[]) => {
  const isNumericValue =
    typeof value === 'number' || (!isNaN(Number(value)) && value !== '' && value !== null);

  return isNumericValue ? 'number' : 'text';
};

export const isNetworkError = (error: unknown): boolean => {
  if (!navigator.onLine) {
    return true;
  }

  // if (error instanceof XMLHttpRequest) {
  //   if (error.status === 0 || !error.responseURL) {
  //     return true;
  //   }
  // }

  // If someone decides to bring back axios etc. this sections still
  // works like a fuzzy net of common errors
  const errorMessage = (error as { message?: string }).message?.toLowerCase?.() || '';
  const networkErrorMessages = [
    'network error',
    'failed to fetch',
    'connection error',
    'timeout',
    'net::err_',
    'network request failed',
    'connection refused',
    'connection reset',
    'no internet',
    'offline',
  ];

  if (networkErrorMessages.some((msg) => errorMessage.includes(msg))) {
    return true;
  }

  return false;
};

export const isConflictError = (error: unknown): boolean => {
  if (!(error instanceof XMLHttpRequest)) return false;
  if (error.status !== 409 || !error.responseText) return false;

  try {
    const parsed = JSON.parse(error.responseText);
    return parsed.error === 'Conflict';
  } catch {
    return false;
  }
};

export const isBadRequestError = (error: unknown): boolean => {
  if (!(error instanceof XMLHttpRequest)) return false;
  if (error.status !== 400 || !error.responseText) return false;

  try {
    const parsed = JSON.parse(error.responseText);
    return parsed.error === 'Bad Request';
  } catch {
    return false;
  }
};

export const isNotFoundError = (error: unknown): boolean => {
  if (!(error instanceof XMLHttpRequest)) return false;
  return error.status === 404;
};

// ==================== Page Error Handling Helpers ====================

export type PageFetchErrorType = 'PAGE_NOT_FOUND' | 'PAGE_INVALID' | 'NETWORK_ERROR' | null;

/**
 * Determines the type of error that occurred during page fetch
 */
export const getPageFetchErrorType = (error: unknown): PageFetchErrorType => {
  if (isNotFoundError(error)) return 'PAGE_NOT_FOUND';
  if (isNetworkError(error)) return 'NETWORK_ERROR';
  return 'PAGE_INVALID';
};

/**
 * Toast configuration for network connection errors
 */
export const getNetworkErrorToast = () => ({
  text: 'Cannot save. Check connection.',
  state: 'warning' as const,
  icon: 'network-lost' as const,
  autoDismiss: false,
  showCloseButton: false,
});

/**
 * Toast configuration for successful save after previous failures
 */
export const getSaveSuccessToast = () => ({
  text: 'Changes saved successfully.',
  state: 'success' as const,
  autoDismiss: true,
  showCloseButton: false,
});

/**
 * Determines the appropriate toast configuration for save failures
 * @param consecutiveFailures - Number of consecutive failures INCLUDING the current one
 */
export const getSaveFailureToast = (consecutiveFailures: number) => {
  if (consecutiveFailures >= 3) {
    // 3+ consecutive failures - show persistent warning
    return {
      text: 'Cannot save. Check connection.',
      state: 'warning' as const,
      autoDismiss: false,
      showCloseButton: false,
    };
  }
  // First or second failure - show dismissable error
  return {
    text: 'Save failed. Please try again.',
    state: 'error' as const,
  };
};

// resolver main Builder
export const resolver = {
  Countdown: EditorCountdown,
  Button: EditorButton,
  Container: EditorContainer,
  Text: EditorText,
  Page: EditorPage,
  Image: EditorImage,
  Icon: EditorIcon,
  Video: EditorVideo,
  Embed: EditorEmbed,
  Form: EditorForm,
  FormTextField: EditorFormTextField,
  FormEmailField: EditorFormEmailField,
  FormConsentField: EditorFormConsentField,
  FormDropdownField: EditorFormDropdownField,
  FormPhoneField: EditorFormPhoneField,
  FormSelectionField: EditorFormSelectionField,
  FormSubmitButton: EditorFormSubmitButton,
  Popup: EditorPopup,
  AiGenerationLoader: AiGenerationLoader, // Experimental AI builder component
};

export const getButtonHeight = (
  topPadding: number,
  bottomPadding: number,
  fontSize: number,
  borderWidth: number,
): number => {
  return topPadding + bottomPadding + fontSize + borderWidth * 2;
};

export const getButtonTypeFromRadius = (
  radius: number,
  buttonHeight: number,
): ButtonType | undefined => {
  if (radius === 0) return 'square';
  const pillThreshold = buttonHeight * 0.5;
  if (radius >= pillThreshold) return 'pill';
  if (radius > 0 && radius < pillThreshold) return 'round';
  return undefined;
};

/**
 * Deep compare two objects/JSON by converting to sorted JSON strings
 * This handles different key orderings
 */
export const isJsonEqual = (obj1: unknown, obj2: unknown): boolean => {
  try {
    // Convert to objects if strings
    const parsed1 = typeof obj1 === 'string' ? JSON.parse(obj1) : obj1;
    const parsed2 = typeof obj2 === 'string' ? JSON.parse(obj2) : obj2;

    // Stringify with sorted keys for consistent comparison
    const sortedStringify = (obj: unknown): string => {
      return JSON.stringify(obj, (_, value) => {
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          return Object.keys(value)
            .sort()
            .reduce(
              (sorted, key) => {
                sorted[key] = value[key];
                return sorted;
              },
              {} as Record<string, unknown>,
            );
        }
        return value;
      });
    };
    return sortedStringify(parsed1) === sortedStringify(parsed2);
  } catch (error) {
    console.error('Error comparing JSON:', error);
    return false;
  }
};
export const isSingleRootContainer = (node: Node, query: NodeQuery) => {
  if (!node || !isContainerNode(node)) return false;

  if (node.data.parent !== ROOT_NODE) return false;

  const rootNode = query.node(ROOT_NODE).get();
  if (!rootNode) return false;

  const rootChilds = rootNode.data.nodes;
  return rootChilds.length === 1;
};

// Helper function to remove undefined values from an object
export const removeUndefined = <T extends Record<string, unknown>>(obj: T): Partial<T> => {
  return Object.fromEntries(
    Object.entries(obj).filter(([, value]) => value !== undefined),
  ) as Partial<T>;
};
